import "react-international-phone/style.css";

import {
  Alert,
  Anchor,
  Button,
  Center,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { Form, useFetcher } from "@remix-run/react";
import { IconExclamationCircle } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { PhoneInput } from "react-international-phone";
import { z } from "zod";

import SmsIcon from "~/assets/misc/sms-icon.svg?react";
import CheckIcon from "~/assets/misc/success-check.svg?react";
import WhatsappIcon from "~/assets/misc/whatsapp-icon.svg?react";
import { useTranslation } from "~/hooks/use-typed-translation";
import { OkDataError } from "~/types";

import classes from "../../routes/($lang).doctor.$slug/book-doctor-detail.module.scss";
import { Trans } from "../typed-trans";

interface PhoneNumberVerificationModalProps {
  opened: boolean;
  close: () => void;
  phoneNumber?: string;
  whatsappPhoneNumber?: string;
  setIsAllowedToBook: (arg: boolean, verifiedNumber?: string) => void;
  timeLeft?: string;
  isComplete?: boolean;
  resetTimer?: () => void;
}

const schema = (t: any) =>
  z.object({
    otp: z.string().min(4, t("verification-code-required")),
  });

type Inputs = z.infer<ReturnType<typeof schema>>;

export const PhoneNumberVerificationModal = ({
  opened,
  close,
  phoneNumber,
  whatsappPhoneNumber,
  setIsAllowedToBook,
  timeLeft,
  isComplete,
  resetTimer,
}: PhoneNumberVerificationModalProps) => {
  const { t } = useTranslation();
  
  // step 0 = Switch Channel/Number Wizard
  // step 1 = OTP Input
  // step 2 = Success
  const [step, setStep] = useState(1);
  const [isFirstOpened, setIsFirstOpened] = useState(false);

  // Initialize internal channel and number based on what the user provided
  const [internalChannel, setInternalChannel] = useState<"whatsapp" | "sms">(
    whatsappPhoneNumber && whatsappPhoneNumber.length > 3 ? "whatsapp" : "sms"
  );
  
  const [activeNumber, setActiveNumber] = useState(
    whatsappPhoneNumber || phoneNumber || ""
  );

  const [fallbackNumber, setFallbackNumber] = useState("");

  // Sync internal state when modal opens
  useEffect(() => {
    if (opened && step === 1 && !isFirstOpened) {
      setInternalChannel(whatsappPhoneNumber && whatsappPhoneNumber.length > 3 ? "whatsapp" : "sms");
      setActiveNumber(whatsappPhoneNumber || phoneNumber || "");
    }
  }, [opened, whatsappPhoneNumber, phoneNumber]);

  const sendOTPFetcher = useFetcher<OkDataError<null>>();
  const verifyOTPFetcher = useFetcher<OkDataError<null>>();

  const form = useForm({
    mode: "controlled",
    validate: zodResolver(schema(t)),
    initialValues: {
      otp: "",
    },
  });

  const handleFormSubmit = (values: Inputs) => {
    verifyOTPFetcher.submit(
      {
        otp: values.otp,
        phoneNumber: activeNumber,
        channel: internalChannel,
      },
      {
        action: `/api/verify-otp`,
        method: "post",
        encType: "application/json",
      }
    );
  };

  const sendOtp = (channelToUse = internalChannel, numberToUse = activeNumber) => {
    if (!numberToUse || numberToUse.length < 5) return; 
    
    sendOTPFetcher.submit(
      { channel: channelToUse },
      {
        action: `/api/send-otp/${encodeURIComponent(numberToUse)}`,
        method: "post",
        encType: "application/json",
      }
    );
    resetTimer && resetTimer();
  };

  const handleSwitchChannelClick = () => {
    setFallbackNumber(activeNumber);
    setStep(0);
  };

  const confirmSwitchChannel = (newChannel: "whatsapp" | "sms") => {
    if (fallbackNumber.length < 8) return; // Basic validation
    
    setInternalChannel(newChannel);
    setActiveNumber(fallbackNumber);
    setStep(1);
    form.reset(); // clear any old OTP errors
    
    sendOtp(newChannel, fallbackNumber);
  };

  useEffect(() => {
    if (verifyOTPFetcher.data?.ok) {
      setStep(2);
    } else if (verifyOTPFetcher.data?.error) {
      if (verifyOTPFetcher.data.error.status === 401 || verifyOTPFetcher.data.error.status === 400) {
         form.setFieldError("otp", verifyOTPFetcher.data.error.message || t("verification-failed"));
      }
    }
  }, [verifyOTPFetcher.data]);

  useEffect(() => {
    if (opened) {
      const currentNumber = whatsappPhoneNumber || phoneNumber || "";
      if (currentNumber) {
        if (isFirstOpened && isComplete && step === 1) {
          sendOtp(internalChannel, activeNumber);
        } else if (!isFirstOpened) {
          setIsFirstOpened(true);
          // Force evaluation of initial channel directly from props to avoid race conditions
          const initialChannel = whatsappPhoneNumber && whatsappPhoneNumber.length > 3 ? "whatsapp" : "sms";
          sendOtp(initialChannel, currentNumber);
        }
      }
    }
  }, [opened]);

  // Clean up state when modal closes
  useEffect(() => {
    if (!opened) {
      setTimeout(() => {
        setStep(1);
        form.reset();
        setIsFirstOpened(false);
      }, 300); // Wait for transition
    }
  }, [opened]);

  // Determine if we should show a generic server error alert
  const hasVerifyServerError = 
    verifyOTPFetcher.data?.error && 
    verifyOTPFetcher.data.error.status !== 401 && 
    verifyOTPFetcher.data.error.status !== 400;

  const hasSendServerError = 
    sendOTPFetcher.data?.error && 
    sendOTPFetcher.data.error.status !== 401 && 
    sendOTPFetcher.data.error.status !== 400;

  return (
    <Modal
      opened={opened}
      onClose={close}
      closeOnClickOutside={true}
      closeOnEscape={true}
      title=""
      centered
      withCloseButton={true}
      styles={{
        body: { padding: "0 1.5rem 2rem", minHeight: 400 - 60 },
      }}>
      
      {step === 0 ? (
        <Stack ta="center" gap={"1.5rem"} align="center">
          <Title order={1} fw={600} fz={18} mt="1rem">
            {t("choose-verification-method")}
          </Title>
          <Text fz={14} c="dimmed" mt="-1rem">
            {t("enter-phone-and-choose-method")}
          </Text>

          <div style={{ width: "100%", textAlign: "left" }}>
            <PhoneInput
              className={classes["react-international-phone"]}
              countrySelectorStyleProps={{
                className: classes["react-international-phone-country-selector"],
              }}
              inputClassName={classes["react-international-phone-input"]}
              defaultCountry="ci"
              value={fallbackNumber}
              onChange={setFallbackNumber}
            />
          </div>

          <Stack w="100%" gap="xs" mt="0.5rem">
            <Button
              variant="light"
              color="green"
              size="lg"
              leftSection={<WhatsappIcon width={24} height={24} />}
              onClick={() => confirmSwitchChannel("whatsapp")}
              disabled={fallbackNumber.length < 8 || sendOTPFetcher.state === "submitting"}
              loading={sendOTPFetcher.state === "submitting"}
            >
              {t("send-via-whatsapp")}
            </Button>
            <Button
              variant="light"
              color="blue"
              size="lg"
              leftSection={<SmsIcon width={24} height={24} />}
              onClick={() => confirmSwitchChannel("sms")}
              disabled={fallbackNumber.length < 8 || sendOTPFetcher.state === "submitting"}
              loading={sendOTPFetcher.state === "submitting"}
            >
              {t("send-via-sms")}
            </Button>
          </Stack>

          <Button 
            variant="subtle" 
            onClick={() => setStep(1)} 
            disabled={sendOTPFetcher.state === "submitting"}
          >
            {t("cancel")}
          </Button>
        </Stack>
      ) : null}

      {step === 1 ? (
        <Form
          onSubmit={form.onSubmit(
            values => handleFormSubmit(values),
            errors => {
              const firstErrorPath = Object.keys(errors)[0];
              form.getInputNode(firstErrorPath)?.focus();
            }
          )}>
          <Center>
            {internalChannel === "whatsapp" 
              ? <WhatsappIcon width={60} height={60} /> 
              : <SmsIcon width={60} height={60} />
            }
          </Center>
          <Stack ta="center" gap={"1.5rem"} align="center">
            <Title order={1} fw={600} fz={18} mt="1.5rem">
              {t("verify-your-phone-number")}
            </Title>

            <Stack gap={5} w="100%" align="center">
              <Text fz={14}>
                <Trans
                  i18nKey="verification-code-sent-via-channel"
                  values={{ 
                    phoneNumber: activeNumber,
                    channel: internalChannel === "whatsapp" ? "WhatsApp" : "SMS" 
                  }}
                  components={{ bold: <Text span inherit fw={500} /> }}
                />
              </Text>
              
              <Anchor
                component="button"
                type="button"
                fz={13}
                c="blue"
                fw={500}
                td="underline"
                disabled={sendOTPFetcher.state === "submitting" || verifyOTPFetcher.state === "submitting"}
                onClick={handleSwitchChannelClick}>
                {t("try-another-method")}
              </Anchor>
            </Stack>

            <TextInput
              label={t("your-verification-code")}
              withAsterisk
              type="number"
              size="md"
              w="100%"
              ta="left"
              mt="0.5rem"
              error={form.errors.otp}
              {...form.getInputProps("otp")}
              key={form.key("otp")}
            />

            {hasVerifyServerError && (
              <Alert icon={<IconExclamationCircle size={16} />} title="Error" color="red" w="100%">
                {verifyOTPFetcher.data?.error?.message}
              </Alert>
            )}
            {hasSendServerError && (
              <Alert icon={<IconExclamationCircle size={16} />} title="Error" color="red" w="100%">
                {sendOTPFetcher.data?.error?.message}
              </Alert>
            )}

            <Group w="100%">
              <Button
                flex={1}
                type="button"
                variant="outline"
                mt="0.5rem"
                disabled={
                  verifyOTPFetcher.state === "submitting" || !isComplete || sendOTPFetcher.state === "submitting"
                }
                onClick={() => sendOtp()}>
                {isComplete
                  ? t("resend")
                  : t("resend-in-x", { time: timeLeft })}
              </Button>
              <Button
                flex={1}
                type="submit"
                mt="0.5rem"
                disabled={form.values.otp.length < 4}
                loading={verifyOTPFetcher.state === "submitting"}>
                {t("verify")}
              </Button>
            </Group>
          </Stack>
        </Form>
      ) : null}

      {step === 2 ? (
        <div>
          <Center>
            <CheckIcon width={60} height={60} />
          </Center>
          <Stack ta="center" gap={"1.5rem"}>
            <Title order={1} fw={600} fz={18} mt="1.5rem">
              {t("phone-number-verified")}
            </Title>

            <Text fz={14}>{t("phone-number-verified-description")}</Text>

            <Text fz={12} c="dimmed" fs="italic" mt="-0.5rem">
              {t("mobile-app-login-notice")}
            </Text>

            <Button
              mt="0.5rem"
              onClick={() => {
                // Pass the verified number back to the parent so the booking payload is accurate
                setIsAllowedToBook(true, activeNumber);
                close();
              }}>
              {t("confirm-appointment")}
            </Button>
          </Stack>
        </div>
      ) : null}
    </Modal>
  );
};
